import { getLocalFileUrl, isValidURL } from './helpers/url';
import type { ToastMethods } from './hooks/menu';

export interface DownloadLink {
  downloadPath: string;
  filename: string;
}

interface QueueItem {
  downloadLink: DownloadLink;
  resolve: () => void;
  reject: (error: Error) => void;
}

interface QueueAndDownloadParams {
  downloadLinks: DownloadLink[];
  manager: DownloadManager;
  toastr: ToastMethods;
}

export class DownloadManager {
  private queue: QueueItem[];
  private activeDownloads: number;
  private concurrency: number;
  public successFullDownloads: number;
  public failedDownloads: number;
  private browserDownloadQ: boolean;
  private min: number;
  private max: number;

  constructor(concurrency = 6, browserDownloadQ = true, min = 500, max = 1300) {
    this.queue = [];
    this.activeDownloads = 0;
    this.concurrency = concurrency;
    this.successFullDownloads = 0;
    this.failedDownloads = 0;
    this.browserDownloadQ = browserDownloadQ;
    this.min = min;
    this.max = max;
  }

  addToQueue(downloadLink: DownloadLink): Promise<void> {
    return new Promise((resolve, reject) => {
      this.queue.push({ downloadLink, resolve, reject });
      this.processQueue();
    });
  }

  private processQueue(): void {
    if (this.activeDownloads >= this.concurrency || this.queue.length === 0) {
      return;
    }

    const queueItem = this.queue.shift();
    if (!queueItem) return;

    const { downloadLink, resolve, reject } = queueItem;
    this.activeDownloads++;

    const downloadFunc = this.browserDownloadQ ? this.toBrowserDownloadQueue.bind(this) : this.downloadFile.bind(this);

    downloadFunc(downloadLink)
      .then(() => {
        this.successFullDownloads++;
        resolve();
      })
      .catch((error: Error) => {
        this.failedDownloads++;
        reject(error);
      })
      .finally(() => {
        this.activeDownloads--;
        this.processQueue();
      });
  }

  private toBrowserDownloadQueue(downloadLink: DownloadLink): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const { downloadPath, filename } = downloadLink;
        let downloadUrl = downloadPath;

        if (!downloadUrl || typeof downloadUrl !== 'string' || downloadUrl.trim() === '') {
          throw new Error('Empty download URL');
        }

        if (!isValidURL(downloadUrl)) {
          downloadUrl = getLocalFileUrl(downloadUrl);
        }

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);

        const jitter = Math.floor(Math.random() * (this.max - this.min + 1)) + this.min;

        setTimeout(() => {
          link.click();
          resolve();
        }, jitter);
      } catch (error) {
        console.error(error);
        reject(error instanceof Error ? error : new Error('Download failed'));
      }
    });
  }

  private async downloadFile(downloadLink: DownloadLink): Promise<void> {
    const { downloadPath: url, filename } = downloadLink;
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch ${url}`);
      }

      const blob = await response.blob();
      const link = document.createElement('a');
      const urlObject = URL.createObjectURL(blob);

      link.href = urlObject;
      link.download = filename;
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        URL.revokeObjectURL(urlObject);
        document.body.removeChild(link);
      }, 100);

      return Promise.resolve();
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

export const queueAndDownload = ({ downloadLinks, manager, toastr }: QueueAndDownloadParams): DownloadManager => {
  const validLinks = Array.isArray(downloadLinks)
    ? downloadLinks.filter(
        (l): l is DownloadLink =>
          l !== null && l !== undefined && typeof l.downloadPath === 'string' && l.downloadPath.trim() !== '',
      )
    : [];

  if (validLinks.length === 0) {
    return manager;
  }

  validLinks.forEach((downloadLink) => {
    manager
      .addToQueue(downloadLink)
      .catch((error: Error) => toastr.error(`Failed to download ${downloadLink.filename} - ${error.message}`))
      .then(() => toastr.info(`Added ${downloadLink.filename} to download queue`));
  });

  return manager;
};
